import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Projet } from '../types';
import { useDashboardCalculations } from '../hooks/useDashboardCalculations';
import DashboardKpiCards from './DashboardKpiCards';
import DashboardSidebarUrgences from './DashboardSidebarUrgences';
import DashboardSanteFinanciere from './DashboardSanteFinanciere';
import GraphiqueModal from './GraphiqueModal';
import ProjetCard from './ProjetCard';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  MapPinIcon,
  FunnelIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';

interface DashboardProps {
  projets: Projet[];
  onOuvrirProjet: (projetId: string) => void;
}

const statutLabel: Record<string, string> = {
  en_cours: 'En cours',
  termine: 'Terminé',
  en_pause: 'En pause',
  a_risque: 'À risque',
};

const statutBadge: Record<string, { bg: string; text: string }> = {
  en_cours: { bg: 'bg-blue-100', text: 'text-blue-800' },
  termine: { bg: 'bg-green-100', text: 'text-green-800' },
  en_pause: { bg: 'bg-gray-100', text: 'text-gray-800' },
  a_risque: { bg: 'bg-red-100', text: 'text-red-800' },
};

function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const Dashboard: React.FC<DashboardProps> = ({ projets, onOuvrirProjet }) => {
  const { kpis, alertes, santeFinanciere, projetsCalcules } =
    useDashboardCalculations(projets);

  const [modaleGraphique, setModaleGraphique] = useState<{ type: 'marge' | 'sante' } | null>(null);
  const [recherche, setRecherche] = useState('');
  const [filtreStatut, setFiltreStatut] = useState<string>('tous');
  const [tri, setTri] = useState<string>('nom');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filtrage des projets
  const rechercheNorm = normalize(recherche.trim());

  const projetsFiltres = useMemo(() => {
    let result = projetsCalcules;

    // Filtre par recherche
    if (rechercheNorm) {
      result = result.filter(p =>
        normalize(p.nom).includes(rechercheNorm) ||
        normalize(p.adresse).includes(rechercheNorm)
      );
    }

    // Filtre par statut
    if (filtreStatut !== 'tous') {
      result = result.filter(p => p.statut === filtreStatut);
    }

    // Tri
    const sorted = [...result];
    switch (tri) {
      case 'mat':
        sorted.sort((a, b) => a.mat.margeATerminaison - b.mat.margeATerminaison);
        break;
      case 'mat_desc':
        sorted.sort((a, b) => b.mat.margeATerminaison - a.mat.margeATerminaison);
        break;
      case 'marge':
        sorted.sort((a, b) => a.margeGlobale - b.margeGlobale);
        break;
      case 'marge_desc':
        sorted.sort((a, b) => b.margeGlobale - a.margeGlobale);
        break;
      case 'alertes':
        sorted.sort((a, b) => b.alertes.length - a.alertes.length);
        break;
      default:
        break;
    }

    return sorted;
  }, [rechercheNorm, projetsCalcules, filtreStatut, tri]);

  const suggestions = projetsFiltres;

  // Fermer suggestions au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Navigation clavier dans les suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      onOuvrirProjet(suggestions[selectedIndex].id);
      setShowSuggestions(false);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const highlightMatch = (text: string) => {
    if (!rechercheNorm) return text;
    const normText = normalize(text);
    const idx = normText.indexOf(rechercheNorm);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-violet-200 text-violet-900 rounded px-0.5">
          {text.slice(idx, idx + rechercheNorm.length)}
        </span>
        {text.slice(idx + rechercheNorm.length)}
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* ═══ SECTION 1 : HEADER KPIs ═══ */}
      <DashboardKpiCards kpis={kpis} onOuvrirGraphique={(type) => setModaleGraphique({ type })} />

      {/* ═══ SECTIONS 2 + 4 + 5 : Grille principale ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Colonne gauche : Santé Financière + Performance */}
        <div className="lg:col-span-2 space-y-6">
          {/* Section 4 : Santé Financière */}
          <DashboardSanteFinanciere
            data={santeFinanciere}
            onOuvrirProjet={onOuvrirProjet}
            onOuvrirGraphique={(type) => setModaleGraphique({ type })}
          />
        </div>

        {/* Colonne droite : Sidebar Urgences (stretch pour matcher la hauteur gauche) */}
        <div className="lg:col-span-1">
          <DashboardSidebarUrgences
            alertes={alertes}
            onOuvrirProjet={onOuvrirProjet}
          />
        </div>
      </div>

      {/* ═══ SECTION PROJETS ═══ */}
      <section className="p-6 bg-white shadow rounded-xl">
        {/* Header : titre + compteur + filtres + recherche */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex-shrink-0">Mes projets</h2>
          <span className="text-sm text-gray-500 flex-shrink-0">
            {projetsFiltres.length} projet{projetsFiltres.length > 1 ? 's' : ''}
            {(rechercheNorm || filtreStatut !== 'tous') && ` sur ${projetsCalcules.length}`}
          </span>

          {/* Filtre statut */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <FunnelIcon className="w-4 h-4 text-gray-400" />
            {['tous', 'en_cours', 'termine', 'en_pause', 'a_risque'].map(s => (
              <button
                key={s}
                onClick={() => setFiltreStatut(s)}
                className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                  filtreStatut === s
                    ? 'bg-violet-100 text-violet-700 font-medium'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {s === 'tous' ? 'Tous' : statutLabel[s]}
              </button>
            ))}
          </div>

          {/* Tri */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
            <select
              value={tri}
              onChange={(e) => setTri(e.target.value)}
              className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white text-gray-600"
            >
              <option value="nom">Nom (défaut)</option>
              <option value="mat_desc">MAT ↓ (meilleur)</option>
              <option value="mat">MAT ↑ (pire)</option>
              <option value="marge_desc">Marge ↓ (meilleure)</option>
              <option value="marge">Marge ↑ (pire)</option>
              <option value="alertes">Alertes (plus d'abord)</option>
            </select>
          </div>

          {/* Recherche compacte */}
          <div ref={containerRef} className="relative ml-auto w-64">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={recherche}
                onChange={(e) => {
                  setRecherche(e.target.value);
                  setShowSuggestions(true);
                  setSelectedIndex(-1);
                }}
                onFocus={() => recherche.trim() && setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                placeholder="Rechercher un projet..."
                className="w-full pl-8 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              {recherche && (
                <button
                  onClick={() => {
                    setRecherche('');
                    setShowSuggestions(false);
                    inputRef.current?.focus();
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dropdown autocomplétion */}
            {showSuggestions && rechercheNorm && suggestions.length > 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
                {suggestions.map((projet, i) => {
                  const badge = statutBadge[projet.statut] || statutBadge.en_cours;
                  return (
                    <div
                      key={projet.id}
                      onMouseEnter={() => setSelectedIndex(i)}
                      onClick={() => {
                        onOuvrirProjet(projet.id);
                        setShowSuggestions(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                        i === selectedIndex ? 'bg-violet-50' : 'hover:bg-gray-50'
                      } ${i > 0 ? 'border-t border-gray-100' : ''}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {highlightMatch(projet.nom)}
                          </span>
                          <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${badge.bg} ${badge.text} flex-shrink-0`}>
                            {statutLabel[projet.statut]}
                          </span>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mt-0.5 truncate">
                          <MapPinIcon className="w-3 h-3 mr-0.5 flex-shrink-0" />
                          {highlightMatch(projet.adresse)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Aucun résultat */}
            {showSuggestions && rechercheNorm && suggestions.length === 0 && (
              <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg px-3 py-4 text-center">
                <p className="text-xs text-gray-500">Aucun projet trouvé</p>
              </div>
            )}
          </div>
        </div>

        {/* Liste des projets filtrés */}
        <div className="space-y-3">
          {projetsFiltres.map(projet => (
            <ProjetCard
              key={projet.id}
              projet={projet}
              onOuvrir={onOuvrirProjet}
            />
          ))}
        </div>
      </section>

      {/* Modale graphique */}
      {modaleGraphique && (
        <GraphiqueModal
          type={modaleGraphique.type}
          historique={santeFinanciere.historiqueMensuel}
          onFermer={() => setModaleGraphique(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
